'use client'

import type {
  ActivityWithProgramCount,
  CampaignBanner,
  FacilityListParams,
  Notification,
  Tag,
  TopActivityRankings,
  TopMeResponse,
} from '@gyym/sdk'
import { Button, Image, Link, Separator, Typography, useDialog } from '@gyym/ui'
import {
  Arrow,
  Facility as FacilityIcon,
  Location,
  Tag as TagIcon,
} from '@gyym/ui/icons'
import anytimeFitnessImg from '@public/assets/images/top/brands/anytime.png'
import bMonsterImg from '@public/assets/images/top/brands/b-monster.png'
import burnesStyleImg from '@public/assets/images/top/brands/burnes-style.png'
import cospaImg from '@public/assets/images/top/brands/cospa.png'
import drStretchImg from '@public/assets/images/top/brands/drstretch.png'
import expaImg from '@public/assets/images/top/brands/expa.png'
import fastgym24Img from '@public/assets/images/top/brands/fastgym24.png'
import fitbase24Img from '@public/assets/images/top/brands/fitbase24.png'
import goldsGymImg from '@public/assets/images/top/brands/goldsgym.png'
import inseaImg from '@public/assets/images/top/brands/insea.png'
import loiveImg from '@public/assets/images/top/brands/loive.png'
import megalosImg from '@public/assets/images/top/brands/megalos.png'
import nasImg from '@public/assets/images/top/brands/nas.png'
import oasisImg from '@public/assets/images/top/brands/oasis.png'
import orangeTheoryFitnessImg from '@public/assets/images/top/brands/orangetheoryfitness.png'
import renaissanceImg from '@public/assets/images/top/brands/renaissance.png'
import surfFitImg from '@public/assets/images/top/brands/surffit.png'
import theBarMethodImg from '@public/assets/images/top/brands/thebarmethod.png'
import tipnessImg from '@public/assets/images/top/brands/tipness.png'
import aichiButtonImg from '@public/assets/images/top/button_aichi.png'
import chibaButtonImg from '@public/assets/images/top/button_chiba.png'
import fukuokaButtonImg from '@public/assets/images/top/button_fukuoka.png'
import hyogoButtonImg from '@public/assets/images/top/button_hyogo.png'
import kanagawaButtonImg from '@public/assets/images/top/button_kanagawa.png'
import kyotoButtonImg from '@public/assets/images/top/button_kyoto.png'
import miyagiButtonImg from '@public/assets/images/top/button_miyagi.png'
import osakaButtonImg from '@public/assets/images/top/button_osaka.png'
import saitamaButtonImg from '@public/assets/images/top/button_saitama.png'
import tokyoButtonImg from '@public/assets/images/top/button_tokyo.png'
import searchAreaImg from '@public/assets/images/top/search_area.svg'
import searchConditionImg from '@public/assets/images/top/search_condition.svg'
import searchFacilityImg from '@public/assets/images/top/search_facility.svg'
import searchLocationImg from '@public/assets/images/top/search_location.svg'
import type { StaticImageData } from 'next/image'
import { useRouter, useSearchParams } from 'next/navigation'
import React from 'react'

import { FacilityCardSwiper } from '@/components/UI/FacilityCardSwiper'
import { useAuthUser } from '@/modules/Auth'
import { getFacilityAwardYear } from '@/modules/Awards'
import { BaseFacility, buildFacilityLink } from '@/modules/Facilities'
import {
  searchHistory,
  searchQueryConverter,
  useSearchAreaModal,
  useSearchConditionsModal,
  useSearchFacilityNameModal,
} from '@/modules/FacilitySearch'
import { useToggleFavorite } from '@/modules/Favorites'
import { useGyymPassModalEffect } from '@/modules/Me'
import { usePrefectures } from '@/modules/Prefectures'

import { ActivityRankingSlide } from './ActivityRankingSlide'
import { ActivitySwiper } from './ActivitySwiper'
import { CampaignBannerSwiper } from './CampaignBannerSwiper'
import { FacilitySlide } from './FacilitySlide'
import { FirstView } from './FirstView'
import { ReservedProgramsSwiper } from './ReservedProgramsSwiper'
import { Section } from './Section'
import {
  AreaBlock,
  AreaButton,
  AreaButtonLabel,
  AreaButtons,
  Container,
  ImageContainer,
  SearchButtons,
  SearchCardButton,
  SearchLabel,
  SearchWrapper,
  StyledFacilityLpBanner,
} from './styles'
import { TopButton } from './TopButton'
import { UsageSteps } from './UsageSteps'

const halfSize = (image: StaticImageData) => ({
  ...image,
  height: image.height / 2,
  width: image.width / 2,
})

const brandImages: { [key: string]: StaticImageData } = {
  tipness: halfSize(tipnessImg),
  megalos: halfSize(megalosImg),
  anytime: halfSize(anytimeFitnessImg),
  goldsgym: halfSize(goldsGymImg),
  cospa: halfSize(cospaImg),
  fitbase24: halfSize(fitbase24Img),
  'b-monster': halfSize(bMonsterImg),
  expa: halfSize(expaImg),
  fastgym24: halfSize(fastgym24Img),
  insea: halfSize(inseaImg),
  surffit: halfSize(surfFitImg),
  loive: halfSize(loiveImg),
  burnesstyle: halfSize(burnesStyleImg),
  orangetheoryfitness: halfSize(orangeTheoryFitnessImg),
  oasis: halfSize(oasisImg),
  nas: halfSize(nasImg),
  renaissance: halfSize(renaissanceImg),
  thebarmethod: halfSize(theBarMethodImg),
  drstretch: halfSize(drStretchImg),
}

export type TopViewProps = {
  me?: TopMeResponse
  notifications: Notification[]
  nearbyFacilities: BaseFacility[]
  recommendedFacilities: BaseFacility[]
  activityRankings: TopActivityRankings[]
  campaignBanners: CampaignBanner[]
  activities: ActivityWithProgramCount[]
  newFacilities: BaseFacility[]
  brandTags: Tag[]
  conditionTags: Tag[]
}

const popularAreas = [
  {
    slug: 'tokyo',
    name: '東京',
    image: halfSize(tokyoButtonImg),
  },
  {
    slug: 'kanagawa',
    name: '神奈川',
    image: halfSize(kanagawaButtonImg),
  },
  {
    slug: 'chiba',
    name: '千葉',
    image: halfSize(chibaButtonImg),
  },
  {
    slug: 'saitama',
    name: '埼玉',
    image: halfSize(saitamaButtonImg),
  },
  {
    slug: 'osaka',
    name: '大阪',
    image: halfSize(osakaButtonImg),
  },
  {
    slug: 'kyoto',
    name: '京都',
    image: halfSize(kyotoButtonImg),
  },
  {
    slug: 'hyogo',
    name: '兵庫',
    image: halfSize(hyogoButtonImg),
  },
  {
    slug: 'aichi',
    name: '愛知',
    image: halfSize(aichiButtonImg),
  },
  {
    slug: 'fukuoka',
    name: '福岡',
    image: halfSize(fukuokaButtonImg),
  },
  {
    slug: 'miyagi',
    name: '宮城',
    image: halfSize(miyagiButtonImg),
  },
]

const TopView = ({
  me,
  notifications,
  nearbyFacilities,
  recommendedFacilities,
  activityRankings,
  campaignBanners,
  activities,
  newFacilities,
  brandTags,
  conditionTags,
}: TopViewProps) => {
  const { isLoggedIn } = useAuthUser()

  const { data: prefectures } = usePrefectures()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { openDialog } = useDialog()

  const openAreaModal = useSearchAreaModal()
  const openFacilityNameModal = useSearchFacilityNameModal()
  const openConditionsModal = useSearchConditionsModal()

  const search = React.useCallback(
    (values: FacilityListParams) => {
      const { as } = searchQueryConverter.toLink(values, prefectures ?? [])

      // Navigate to search result
      router.push(as)
    },
    [prefectures, router],
  )

  const searchWithHistory = React.useCallback(
    (values: FacilityListParams) => {
      // Set search history
      searchHistory.set(values)

      search(values)
    },
    [search],
  )

  const {
    onToggle: onToggleFavorite,
    favoritedFacilityIds,
    isFavoriteLoading,
  } = useToggleFavorite()

  const activityRankingsSlides = React.useMemo(
    () =>
      activityRankings.map(({ topFacility: facility, ...rank }) => (
        <ActivityRankingSlide
          key={facility.id}
          id={facility.id}
          activityRank={1}
          activityRankingName={rank.name}
          activityId={rank.id}
          name={facility.name}
          images={facility.images}
          isFavorited={favoritedFacilityIds.includes(facility.id)}
          onFavoriteClick={onToggleFavorite}
          isFavoriteLoading={isFavoriteLoading}
          station={facility.station}
          reviewCount={facility.reviewCount}
          reviewScore={facility.reviewScore}
          isOnline
          {...buildFacilityLink(facility)}
        />
      )),
    [
      activityRankings,
      favoritedFacilityIds,
      onToggleFavorite,
      isFavoriteLoading,
    ],
  )

  // Triggered when `?corpEntryDone=true` query exists
  // Currently, this query is passed from the vue app
  React.useEffect(() => {
    if (!searchParams?.get('corpEntryDone')) return

    openDialog({
      title: '法人会員への切り替えが完了しました。',
      body: '法人会員の特典は、企業様ごとに異なります。詳細は担当者様へご確認ください。',
      positiveAction: {
        onClick: () => {
          router.replace('/')
        },
      },
    })
  }, [searchParams, router, openDialog])

  useGyymPassModalEffect()

  return (
    <Container>
      <FirstView me={me} notifications={notifications} />

      {isLoggedIn && me && (
        <div className="mt-9">
          <ReservedProgramsSwiper
            currentReservations={me.currentReservations}
          />
          <TopButton mt={10} href="/facilities?sortBy=distance&order=asc">
            近くのジムを探す
          </TopButton>
        </div>
      )}

      <div className="mt-[60px]">
        <CampaignBannerSwiper campaignBanners={campaignBanners} />
      </div>

      {nearbyFacilities.length > 0 && (
        <Section mt={15}>
          <Section.Heading>あなたにおすすめの施設</Section.Heading>

          <Section.Content>
            <FacilityCardSwiper
              id="NearbyFacilities"
              slides={nearbyFacilities.map((facility) => (
                <FacilitySlide
                  key={facility.id}
                  {...facility}
                  awardYear={getFacilityAwardYear(facility.id)}
                  {...buildFacilityLink(facility)}
                />
              ))}
            />
          </Section.Content>
        </Section>
      )}

      <Section mt={15}>
        <Section.Heading number={1} subheading="Recommended">
          スタッフおすすめの施設
        </Section.Heading>

        <Section.Content>
          <FacilityCardSwiper
            id="Recommended"
            slides={recommendedFacilities.map((facility) => (
              <FacilitySlide
                key={facility.id}
                {...facility}
                awardYear={getFacilityAwardYear(facility.id)}
                {...buildFacilityLink(facility)}
              />
            ))}
          />

          <div className="mt-6 flex items-center justify-center">
            <Button variant="primary-outlined" asChild>
              <Link href="/recommended">
                もっと見る <Arrow />
              </Link>
            </Button>
          </div>
        </Section.Content>
      </Section>

      <Section mt={15}>
        <Section.Heading number={2} subheading="New">
          新着の施設
        </Section.Heading>

        <Section.Content>
          <FacilityCardSwiper
            id="NewFacilities"
            slides={newFacilities.map((facility) => (
              <FacilitySlide
                key={facility.id}
                {...facility}
                awardYear={getFacilityAwardYear(facility.id)}
                {...buildFacilityLink(facility)}
              />
            ))}
          />

          <div className="mt-6 flex items-center justify-center">
            <Button variant="primary-outlined" asChild>
              <Link href="/facilities?sortBy=createdAt&order=desc">
                もっと見る <Arrow />
              </Link>
            </Button>
          </div>
        </Section.Content>
      </Section>

      <Section mt={15}>
        <Section.Heading number={3} subheading="Ranking">
          人気のアクティビティ×施設
        </Section.Heading>

        <Section.Content>
          <FacilityCardSwiper
            id="ActivityRankings"
            slides={activityRankingsSlides}
          />
        </Section.Content>
      </Section>

      {!isLoggedIn && (
        <Section mt={15}>
          <Section.Heading number={4} subheading="Steps">
            ご利用ステップ
          </Section.Heading>

          <Section.Content>
            <UsageSteps />

            <TopButton mt={8} href="/facilities?sortBy=distance&order=asc">
              近くのジムを探す
            </TopButton>
          </Section.Content>
        </Section>
      )}

      <Section backgroundType="alt" mt={15} pt={10} pb={15} gap={10}>
        <Section.Heading number={isLoggedIn ? 4 : 5} subheading="Search">
          施設を検索
        </Section.Heading>

        <Section.Content>
          <SearchWrapper className="flex-col gap-10 px-4">
            <SearchButtons>
              <SearchCardButton
                role="button"
                type="button"
                onClick={() => {
                  openAreaModal({
                    onSubmit: searchWithHistory,
                  })
                }}
              >
                <ImageContainer>
                  <Image
                    src={searchAreaImg}
                    alt="エリアから検索"
                    width={75}
                    height={67}
                  />
                </ImageContainer>
                <SearchLabel>エリアから</SearchLabel>
              </SearchCardButton>

              <SearchCardButton
                role="button"
                type="button"
                onClick={() => {
                  router.push('/facilities?sortBy=distance&order=asc')
                }}
              >
                <ImageContainer>
                  <Image
                    src={searchLocationImg}
                    alt="現在地から検索"
                    width={75}
                    height={67}
                  />
                </ImageContainer>
                <SearchLabel>現在地から</SearchLabel>
              </SearchCardButton>

              <SearchCardButton
                role="button"
                type="button"
                onClick={() => {
                  openConditionsModal({
                    onSubmit: searchWithHistory,
                  })
                }}
              >
                <ImageContainer>
                  <Image
                    src={searchConditionImg}
                    alt="条件から検索"
                    width={75}
                    height={67}
                  />
                </ImageContainer>
                <SearchLabel>条件から</SearchLabel>
              </SearchCardButton>

              <SearchCardButton
                role="button"
                type="button"
                onClick={() => {
                  openFacilityNameModal({
                    onHistoryNavigate: search,
                    onSubmit: searchWithHistory,
                  })
                }}
              >
                <ImageContainer>
                  <Image
                    src={searchFacilityImg}
                    alt="施設名から検索"
                    width={75}
                    height={67}
                  />
                </ImageContainer>
                <SearchLabel>施設名から</SearchLabel>
              </SearchCardButton>
            </SearchButtons>

            <Separator variant="primary" orientation="horizontal" />

            <div className="flex flex-col items-center justify-center gap-10 pc:flex-row pc:items-start pc:justify-between">
              <AreaBlock>
                <div className="flex justify-center">
                  <Typography
                    as="h3"
                    variant="subhead2"
                    color="blue.01"
                    gap={2}
                    inlineFlex
                  >
                    <Location height="20px" width="20px" />
                    人気のエリア
                  </Typography>
                </div>

                <AreaButtons>
                  {popularAreas.map(({ slug, name, image }) => (
                    <Link key={slug} href={`/facilities/${slug}`}>
                      <AreaButton>
                        <AreaButtonLabel>{name}</AreaButtonLabel>
                        <Image src={image} alt={name} />
                      </AreaButton>
                    </Link>
                  ))}
                </AreaButtons>
              </AreaBlock>

              <Separator
                variant="primary"
                orientation="horizontal"
                pcOrientation="vertical"
                decorative
              />

              <div className="flex w-full flex-col gap-6">
                <div className="flex justify-center">
                  <Typography
                    as="h3"
                    variant="subhead2"
                    color="blue.01"
                    gap={2}
                    inlineFlex
                  >
                    <TagIcon height="20px" width="20px" />
                    こだわり条件
                  </Typography>
                </div>

                {conditionTags && (
                  <div className="flex flex-wrap justify-center gap-2">
                    {conditionTags.map((tag) => (
                      <Button
                        key={tag.id}
                        variant="primary-outlined"
                        size="sm"
                        asChild
                      >
                        <Link
                          {...searchQueryConverter.toLink(
                            { tags: [tag.value] },
                            prefectures ?? [],
                          )}
                        >
                          {tag.label}
                        </Link>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Separator variant="primary" orientation="horizontal" />

            <div className="flex flex-col justify-center gap-6">
              <div className="flex justify-center">
                <Typography
                  as="h3"
                  variant="subhead2"
                  color="blue.01"
                  gap={2}
                  inlineFlex
                >
                  <FacilityIcon height="20px" width="20px" />
                  ブランドから探す
                </Typography>
              </div>

              {brandTags && (
                <div className="flex w-full flex-wrap items-center justify-center gap-4">
                  {brandTags
                    .map((tag) =>
                      brandImages[tag.value] ? (
                        <Link
                          key={tag.id}
                          {...searchQueryConverter.toLink(
                            { tags: [tag.value] },
                            prefectures ?? [],
                          )}
                        >
                          <Image
                            src={brandImages[tag.value]!}
                            alt={tag.label}
                          />
                        </Link>
                      ) : null,
                    )
                    .filter(Boolean)}
                </div>
              )}
            </div>
          </SearchWrapper>
        </Section.Content>
      </Section>

      <Section mt={15}>
        <Section.Heading number={isLoggedIn ? 5 : 6} subheading="Activities">
          アクティビティ
        </Section.Heading>

        <Section.Content>
          <ActivitySwiper activities={activities} />
        </Section.Content>
      </Section>

      <TopButton mt={10} href="/facilities?sortBy=distance&order=asc">
        近くのジムを探す
      </TopButton>

      <StyledFacilityLpBanner utmCampaign="top_bottom" />
    </Container>
  )
}

export default TopView
